import { KeyboardEvent } from 'react';
import { alpha, InputBase } from '@mui/material';
import {
  gridExpandedSortedRowIdsSelector,
  GridRowId,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';

interface Props {
  rowId: GridRowId;
  field: string;
  value: string;
  tabIndex: -1 | 0;
  changed: boolean;
  invalid: boolean;
  isRowHighlighted: (rowId: GridRowId) => boolean;
  onChange: (value: string) => void;
}

export function DiscountMatrixInputCell({
  rowId,
  field,
  value,
  tabIndex,
  changed,
  invalid,
  isRowHighlighted,
  onChange,
}: Props) {
  const apiRef = useGridApiContext();
  const rowIds = useGridSelector(apiRef, gridExpandedSortedRowIdsSelector);
  const rowIndex = rowIds.indexOf(rowId);
  const previousRowId = rowIds[rowIndex - 1];
  const isLast = rowIndex === rowIds.length - 1;
  const highlighted = invalid || changed;
  const previousHighlighted = previousRowId !== undefined && isRowHighlighted(previousRowId);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const nextRowIndex = rowIndex + (event.shiftKey ? -1 : 1);
    const nextRowId = rowIds[nextRowIndex];
    if (nextRowId === undefined) return;
    apiRef.current.scrollToIndexes({
      rowIndex: nextRowIndex,
      colIndex: apiRef.current.getColumnIndex(field),
    });
    apiRef.current.setCellFocus(nextRowId, field);
  };

  return (
    <InputBase
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onFocus={(event) => event.target.select()}
      onKeyDown={handleKeyDown}
      error={invalid}
      endAdornment="&nbsp;%"
      inputProps={{ tabIndex, inputMode: 'decimal' }}
      sx={(theme) => {
        const palette = theme.vars.palette;
        const changedTint = theme.vars
          ? `rgba(${theme.vars.palette.primary.mainChannel} / 0.08)`
          : alpha(theme.palette.primary.main, 0.08);
        return {
          width: '100%',
          alignSelf: 'stretch',
          px: 1,
          border: `1px solid ${
            invalid ? palette.error.main : changed ? palette.primary.main : palette.divider
          }`,
          borderTopWidth: highlighted || (rowIndex > 0 && !previousHighlighted) ? 1 : 0,
          borderBottomWidth: isLast || highlighted ? 1 : 0,
          backgroundColor: changed ? changedTint : palette.background.paper,
          fontSize: 14,
          fontWeight: 600,
          '&.Mui-focused': { outline: `2px solid ${palette.primary.main}`, outlineOffset: -1 },
          '& input': { textAlign: 'right', py: 0.2 },
          ...theme.applyStyles('dark', {
            borderColor: invalid
              ? palette.error.main
              : changed
              ? palette.primary.light
              : palette.divider,
            '&.Mui-focused': { outlineColor: palette.primary.light },
          }),
        };
      }}
    />
  );
}
